// Per-Bot Performance panel (S8). Presentational only — the parent
// (account-detail) derives the `botPerf` rows from the credential-scoped
// closedPositions set and passes them in. One row per magic; clicking a row
// scopes the page to that bot. The decay Signal is descriptive (L4):
// it reports what the trader's own data shows, never prescribes.
//
// Extracted verbatim from account-detail.tsx (2026-05-28 refactor) — no
// behavioral change. The BotFlag/BotPerfRow types live here because this panel
// is their canonical consumer; the parent imports them back for the memo.

import { cn, fmtMoney } from '@/lib/utils';
import { Panel, PanelBody, PanelHead, SourceBadge } from '@/components/ui';

export type BotFlag = 'decay' | 'cold' | 'ok' | 'building';

export type BotPerfRow = {
  magic: number;
  name: string;                 // ea_name, "Manual", or "Unregistered (magic N)"
  version: string | null;
  trades: number;
  netPnl: number;
  wins: number;
  winPct: number;
  profitFactor: number | null;  // null = no losing trades (rendered as ∞)
  avgNet: number;
  best: number;
  worst: number;
  realizedDdAbs: number;        // worst peak-to-trough of cumulative closed-trade P&L (realized only)
  volume: number;
  lastClose: string | null;     // ISO
  flag: BotFlag;                // descriptive decay signal (L4)
  trailingNet: number;          // net over last 30 days
  trailingTrades: number;
  isSystem?: boolean;           // synthetic "one system" row (all EA magics folded together)
};

export function PerBotPanel({
  rows,
  selectedMagic,
  onSelect,
  collapsed = false,
}: {
  rows: BotPerfRow[];
  selectedMagic: number | 'all';
  onSelect: (m: number | 'all') => void;
  collapsed?: boolean;          // account flagged "one system": EA magics folded into one row
}) {
  if (rows.length === 0) return null;
  return (
    <Panel>
      <PanelHead
        title="Per-Bot Performance"
        sub={collapsed
          ? 'this account is flagged as one system — its bots are folded into a single row; manual is kept separate · drawdown is the combined-book account figure'
          : 'lifetime by magic · click a bot to filter the page · signals are observations, not advice'}
      />
      <PanelBody flush>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b border-line-soft text-[9.5px] uppercase tracking-eyebrow text-text-dim">
                <th className="px-3 py-2 text-left font-bold">Bot</th>
                <th className="px-3 py-2 text-right font-bold">Trades</th>
                <th className="px-3 py-2 text-right font-bold">Win %</th>
                <th className="px-3 py-2 text-right font-bold">Net P&amp;L</th>
                <th
                  className="px-3 py-2 text-right font-bold"
                  title="Worst peak-to-trough of this bot's cumulative closed-trade P&L. Realized only — excludes open-position (floating) drawdown, which is account-level."
                >
                  Realized DD
                </th>
                <th className="px-3 py-2 text-right font-bold">PF</th>
                <th className="px-3 py-2 text-right font-bold">Avg/Pos</th>
                <th className="px-3 py-2 text-right font-bold">Last 30d</th>
                <th className="px-3 py-2 text-left font-bold">Signal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const active = selectedMagic === r.magic;
                const clickable = !r.isSystem;   // the folded system row isn't a single magic to filter on
                return (
                  <tr
                    key={r.magic}
                    onClick={clickable ? () => onSelect(active ? 'all' : r.magic) : undefined}
                    className={cn(
                      'border-b border-line-soft/60 transition-colors',
                      clickable && 'cursor-pointer',
                      active ? 'bg-bg-2' : clickable ? 'hover:bg-bg-2/50' : '',
                    )}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <SourceBadge kind={r.magic === 0 ? 'manual' : 'bot'} />
                        <span className="font-semibold text-text">
                          {r.name}
                          {r.version && <span className="ml-1 text-text-dim">v{r.version}</span>}
                        </span>
                        {r.isSystem ? (
                          <span className="rounded-[3px] bg-blue/10 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.1em] text-blue">
                            System
                          </span>
                        ) : r.magic !== 0 && (
                          <span className="num text-[10px] text-text-dim">#{r.magic}</span>
                        )}
                      </div>
                    </td>
                    <td className="num px-3 py-2 text-right text-text-mid">{r.trades}</td>
                    <td className="num px-3 py-2 text-right text-text-mid">{r.winPct.toFixed(1)}%</td>
                    <td
                      className={cn(
                        'num px-3 py-2 text-right font-semibold',
                        r.netPnl >= 0 ? 'text-green' : 'text-red',
                      )}
                    >
                      {r.netPnl >= 0 ? '+' : ''}{fmtMoney(r.netPnl, '')}
                    </td>
                    <td
                      className={cn(
                        'num px-3 py-2 text-right',
                        r.realizedDdAbs > 0 ? 'text-red' : 'text-text-dim',
                      )}
                      title="Realized (closed-trade) drawdown only — floating/open-position risk is account-level."
                    >
                      {r.realizedDdAbs > 0 ? fmtMoney(-r.realizedDdAbs, '') : '—'}
                    </td>
                    <td className="num px-3 py-2 text-right text-text-mid">
                      {r.profitFactor == null ? '∞' : r.profitFactor.toFixed(2)}
                    </td>
                    <td
                      className={cn(
                        'num px-3 py-2 text-right',
                        r.avgNet >= 0 ? 'text-text-mid' : 'text-red',
                      )}
                    >
                      {r.avgNet >= 0 ? '+' : ''}{r.avgNet.toFixed(2)}
                    </td>
                    <td className="num px-3 py-2 text-right text-text-mid">
                      {r.trailingTrades > 0
                        ? `${r.trailingNet >= 0 ? '+' : ''}${fmtMoney(r.trailingNet, '')} (${r.trailingTrades})`
                        : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <BotSignal flag={r.flag} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PanelBody>
    </Panel>
  );
}

// Signal chip for the Per-Bot panel. Descriptive language only (L4): it
// reports what the trader's own data shows, with the rationale on hover.
function BotSignal({ flag }: { flag: BotFlag }) {
  const map: Record<BotFlag, { label: string; cls: string; title: string }> = {
    decay: {
      label: 'DECAY',
      cls: 'bg-amber/10 text-amber',
      title: 'Last-30-day expectancy per position is materially below the prior period.',
    },
    cold: {
      label: 'COLD',
      cls: 'bg-text-dim/10 text-text-dim',
      title: 'No closed positions in the last 30 days — this bot is dormant.',
    },
    building: {
      label: 'BUILDING',
      cls: 'bg-blue/10 text-blue',
      title: 'Not enough history yet to compare recent vs prior performance (need ≥10 positions on both sides).',
    },
    ok: {
      label: 'OK',
      cls: 'bg-green/10 text-green',
      title: 'Recent performance is in line with the prior period.',
    },
  };
  const s = map[flag];
  return (
    <span
      title={s.title}
      className={cn(
        'inline-block rounded-[3px] px-2 py-[1px] text-[9px] font-bold uppercase tracking-[0.1em]',
        s.cls,
      )}
    >
      {s.label}
    </span>
  );
}
